import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import FashionCard from '../components/FashionCard';
import { ProfileBadges, UserBadges } from '../components/UserBadges';
import { Settings, Camera } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProfilePage = () => {
  const { username } = useParams();
  const { user: currentUser, updateProfile, isAuthenticated } = useAuth();
  const [profile, setProfile] = useState(null);
  const [looks, setLooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState({
    full_name: '',
    bio: '',
    avatar_url: ''
  });

  const isOwnProfile = currentUser?.username === username;

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    try {
      const [profileRes, looksRes] = await Promise.all([
        axios.get(`${API}/users/${username}`),
        axios.get(`${API}/users/${username}/looks`)
      ]);
      setProfile(profileRes.data);
      setLooks(looksRes.data);
      setEditData({
        full_name: profileRes.data.full_name || '',
        bio: profileRes.data.bio || '',
        avatar_url: profileRes.data.avatar_url || ''
      });
    } catch {
      // Error handled via toast
      toast.error('Profile not found');
    } finally {
      setLoading(false);
    }
  }, [username]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await updateProfile(editData);
      toast.success('Profile updated');
      setEditOpen(false);
      fetchProfile();
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 bg-[#0a0a0a]">
        <div className="py-16 px-6 md:px-12">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-8 mb-12">
              <div className="w-24 h-24 rounded-full skeleton" />
              <div className="space-y-3">
                <div className="h-8 w-48 skeleton rounded" />
                <div className="h-4 w-32 skeleton rounded" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen pt-20 bg-[#0a0a0a] flex items-center justify-center">
        <p className="text-gray-500 text-lg">Profile not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-[#0a0a0a]" data-testid="profile-page">
      {/* Profile Header */}
      <div className="py-16 px-6 md:px-12 border-b border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-start gap-8">
            {/* Avatar */}
            <div className="relative">
              <div className="w-24 h-24 md:w-32 md:h-32 rounded-full bg-[#C41E3A] flex items-center justify-center overflow-hidden">
                {profile.avatar_url ? (
                  <img src={profile.avatar_url} alt={profile.username} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-4xl md:text-5xl font-serif text-white">
                    {profile.username[0].toUpperCase()}
                  </span>
                )}
              </div>
            </div>

            {/* Info */}
            <div className="flex-1">
              <div className="flex flex-col md:flex-row md:items-center gap-4 mb-4">
                <div className="flex items-center gap-2">
                  <h1 className="text-3xl md:text-4xl font-serif italic text-white" data-testid="profile-username">
                    @{profile.username}
                  </h1>
                  <UserBadges 
                    role={profile.role} 
                    badges={profile.badges} 
                    isVerified={profile.is_verified} 
                    size="md" 
                    showRole={false} 
                  />
                </div>
                {isOwnProfile && (
                  <Dialog open={editOpen} onOpenChange={setEditOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm" className="gap-2 border-white/20 text-white w-fit" data-testid="edit-profile-btn">
                        <Settings className="w-4 h-4" />
                        Edit Profile
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-[#1c1c1c] border-white/10 text-white">
                      <DialogHeader>
                        <DialogTitle className="font-serif italic text-2xl">Edit Profile</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleEditSubmit} className="space-y-6 pt-4">
                        <div className="space-y-2">
                          <Label className="text-xs uppercase tracking-wider text-gray-400">Full Name</Label>
                          <Input
                            value={editData.full_name}
                            onChange={(e) => setEditData(prev => ({ ...prev, full_name: e.target.value }))}
                            className="bg-[#0a0a0a] border-white/10"
                            placeholder="Your full name"
                            data-testid="edit-fullname-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs uppercase tracking-wider text-gray-400">Bio</Label>
                          <Textarea
                            value={editData.bio}
                            onChange={(e) => setEditData(prev => ({ ...prev, bio: e.target.value }))}
                            className="bg-[#0a0a0a] border-white/10 min-h-[100px]"
                            placeholder="Tell us about your style..."
                            data-testid="edit-bio-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs uppercase tracking-wider text-gray-400">Avatar URL</Label>
                          <Input
                            value={editData.avatar_url}
                            onChange={(e) => setEditData(prev => ({ ...prev, avatar_url: e.target.value }))}
                            className="bg-[#0a0a0a] border-white/10"
                            placeholder="https://..."
                            data-testid="edit-avatar-input"
                          />
                        </div>
                        <Button type="submit" className="w-full btn-luxury btn-luxury-primary" data-testid="save-profile-btn">
                          Save Changes
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                )}
              </div>
              
              {profile.full_name && (
                <p className="text-xl text-gray-300 mb-2">{profile.full_name}</p>
              )}
              
              {profile.bio && (
                <p className="text-gray-400 mb-4 max-w-xl">{profile.bio}</p>
              )}

              <ProfileBadges 
                role={profile.role} 
                badges={profile.badges} 
                isVerified={profile.is_verified} 
              />

              {/* Stats */}
              <div className="flex gap-8 mt-6">
                <div>
                  <p className="text-2xl font-serif text-white">{looks.length}</p>
                  <p className="text-xs uppercase tracking-wider text-gray-500">Looks</p>
                </div>
                <div>
                  <p className="text-2xl font-serif text-white">{profile.followers_count}</p>
                  <p className="text-xs uppercase tracking-wider text-gray-500">Followers</p>
                </div>
                <div>
                  <p className="text-2xl font-serif text-white">{profile.following_count}</p>
                  <p className="text-xs uppercase tracking-wider text-gray-500">Following</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Looks Grid */}
      <div className="py-12 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl font-serif italic text-white mb-8">Fashion Looks</h2>
          
          {looks.length > 0 ? (
            <div className="bento-grid">
              {looks.map((look, index) => (
                <FashionCard key={look.id} look={look} featured={index === 0} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <Camera className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-500">No looks shared yet</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
